import {
	AsyncPipe,
	Location,
	NgFor,
	NgIf,
	NgTemplateOutlet,
} from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import {
	MatMenu,
	MatMenuContent,
	MatMenuItem,
	MatMenuTrigger,
} from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
	ActivatedRoute,
	RouterLink,
	RouterLinkActive,
	RouterOutlet,
} from '@angular/router';
import { DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppIcons,
	AvatarEntity,
	DcAvatarModule,
	DcLayoutModule,
} from '@datachain/ui-sdk/components';
import {
	DcArrayPipeModule,
	DcTruncatePipeModule,
} from '@datachain/ui-sdk/pipes';
import { Store } from '@ngrx/store';
import { Map } from 'immutable';
import {
	map,
	Observable,
	scan,
	shareReplay,
	takeUntil,
	withLatestFrom,
} from 'rxjs';
import { tap } from 'rxjs/operators';

import {
	ActionIconsInjectable,
	AppMarketPlaceIconsInjectable,
	UiComponentIconsInjectable,
} from './app.config';
import { Feature, routes } from './app.routes';
import { InstanceModule } from './core/domain/instance-module';
import { ProductInstanceTypeModel, RoleType } from './core/domain/module-role';
import { UserOrganization } from './core/domain/user-organization';
import { UserProfile } from './core/domain/user-profile';
import { AppSelector } from './core/store';
import { goToConnectedUserProfile } from './core/store/app.actions';
import { DcMarketPlaceIcons } from './ui/app-marketplace-icons';
import { SplashComponent } from './ui/components/splash/splash.component';

type ProfileAction = {
	description: string;
	actionName: string;
	icon: string;
	action: (...args: Array<unknown>) => void;
};

interface INavLink {
	label: string;
	path: string;
	iconName: string | undefined;
}

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss',
	standalone: true,
	imports: [
		AsyncPipe,
		DcAvatarModule,
		NgTemplateOutlet,
		MatMenuItem,
		MatMenuContent,
		MatMenuTrigger,
		MatMenu,
		NgIf,
		NgFor,
		RouterOutlet,
		RouterLink,
		DcArrayPipeModule,
		DcTruncatePipeModule,
		MatTooltipModule,
		FormsModule,
		ReactiveFormsModule,
		MatToolbarModule,
		MatIconModule,
		MatIconButton,
		MatListModule,
		MatDividerModule,
		DcLayoutModule,
		SplashComponent,
		RouterLinkActive,
	],
})
export class AppComponent extends DcBaseComponent {
	public title = 'Dc-MarketPlace';
	public showSplash = true;

	protected readonly DcMarketPlaceIcons = DcMarketPlaceIcons;
	protected readonly ModuleInstanceType = ProductInstanceTypeModel;

	public vo$!: Observable<{
		login: AvatarEntity | null;
		userAvailableInstances: Map<number, InstanceModule>;
	}>;

	public readonly profileActions: Array<ProfileAction> = [
		{
			actionName: 'profile',
			description: $localize`:i18n=@@general.profile:`,
			icon: DcMarketPlaceIcons.Profile,
			action: (): void => {
				this.navigateToProfile();
			},
		},
		{
			actionName: 'logout',
			description: $localize`:i18n=@@general.logout:`,
			icon: DcMarketPlaceIcons.Logout,
			action: (): void => {
				this.logout();
			},
		},
	];

	public navLinks: Array<INavLink> = [];

	public constructor(
		private readonly location: Location,
		private readonly store: Store,
		private readonly appSelector: AppSelector,
		public readonly activatedRoute: ActivatedRoute,
		@Inject(AppMarketPlaceIconsInjectable) private readonly mainIcons: AppIcons,
		@Inject(UiComponentIconsInjectable)
		private readonly componentIcons: AppIcons,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons,
	) {
		super();
		this.cmpId = 'app-main';
		this.mainIcons.addIconsToRegistry();
		this.componentIcons.addIconsToRegistry();
		this.actionIcons.addIconsToRegistry();

		const data$ = this.appSelector.getConnectedUser$().pipe(
			takeUntil(this.onDestroy$),
			withLatestFrom(this.appSelector.getCurrentOrganization$()),
			tap(([user, organisation]) => {
				this.navLinks = this.generateNavLinks(user, organisation);
			}),
			scan(
				(state, [user]) => ({
					avatar: AvatarEntity.build(user.userName),
					instances: user.availableInstanceModules,
				}),
				{
					avatar: {} as AvatarEntity,
					instances: Map<number, InstanceModule>(),
				},
			),
			shareReplay(1),
		);

		this.vo$ = data$.pipe(
			map((data) => ({
				login: data.avatar,
				userAvailableInstances: data.instances,
			})),
		);
	}

	public logout(): void {
		window.location.href = this.location.prepareExternalUrl('/logout');
	}

	public onSplashDismissed(): void {
		this.showSplash = false;
	}

	private navigateToProfile(): void {
		this.store.dispatch(goToConnectedUserProfile());
	}

	private generateNavLinks(
		user: UserProfile,
		organisation: UserOrganization,
	): Array<INavLink> {
		return routes
			.filter((route) => {
				const hasNav = route && route.data && route.data['isPrincipalNav'];
				const hasAccess =
					route &&
					route.data &&
					route.data['roles'] &&
					route.data['roles'].some((role: RoleType) =>
						user.accounts.get(organisation.id)?.includes(role),
					);
				return hasNav && hasAccess;
			})
			.map((r) => {
				let label = 'unnamed';
				if (r.data && r.data['featureName']) {
					label = this.getFeatureName(r.data['featureName']);
				}
				return {
					label,
					path: r.path ? `${r.path}` : '',
					iconName:
						r.data && r.data['iconName'] ? r.data['iconName'] : undefined,
				};
			});
	}

	private getFeatureName(feat: Feature): string {
		switch (feat) {
			case 'sharedElements':
				return $localize`:i18n=@@feature.sharedElements:`;
			case 'admin':
				return $localize`:i18n=@@feature.admin:`;
			default:
				return '';
		}
	}
}
